import { useQuery, useQueryClient } from "react-query";
import { AxiosError } from "axios";
import { fetchFumigationCompanyDetailsService } from "services/fetchFumigationCompanyDetails.service";
import { FumigationCompanyDetail } from "services/fumigationCompany.types";

export interface FumigationCompanyDetailsResponse {
    data: FumigationCompanyDetail[];
}

/** Shared query key — all consumers that use this hook share the same cache entry. */
export const FUMIGATION_COMPANY_DETAILS_KEY = ['fumigationCompanyDetails'] as const;

/**
 * Fetches the full list of fumigation company detail records and caches them.
 *
 * - Uses `useQuery` (not `useMutation`) so React Query handles caching,
 *   deduplication, and background refresh automatically.
 * - All tabs in AdminPortalPage that call this hook share a single network
 *   request; subsequent mounts within the staleTime window read from cache.
 * - Each consumer filters `data` locally for the subset it needs.
 */
const useFumigationCompanyDetails = () => {
    return useQuery<FumigationCompanyDetailsResponse, AxiosError>(
        FUMIGATION_COMPANY_DETAILS_KEY,
        fetchFumigationCompanyDetailsService,
        {
            // Treat data as fresh for 2 minutes — tab switches won't re-fetch.
            staleTime: 2 * 60 * 1000,
            // Keep unused cache entries for 5 minutes (React Query default).
            cacheTime: 5 * 60 * 1000,
            // Don't refetch just because the window regains focus in an admin tool.
            refetchOnWindowFocus: false,
        }
    );
};

/**
 * Call this after any mutation (insert/update/delete) to invalidate the cache
 * and trigger a fresh fetch the next time the data is needed.
 */
export const useInvalidateFumigationCompanyDetails = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries(FUMIGATION_COMPANY_DETAILS_KEY);
};

export default useFumigationCompanyDetails;
